import { useState } from 'react'

import { Button, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import {
  CreateOfferDocument,
  InfojobsOfferDocument,
  InfojobsOfferValuesFragment,
  PublishOfferDocument,
  UnpublishOfferDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Status } from 'modules/ats/lib/enums'
import JobBoardCard from 'modules/ats/pages/JobPostingPromotion/JobBoards/JobBoardCard'
import { IntegrationSetupButton } from 'modules/ats/pages/JobPostingPromotion/JobBoards/Providers/IntegrationSetupButton'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

import {
  FillMissingInformationButton,
  ManageOfferButton,
  OpenExpiredButton,
  PublishButton,
  RetryButton,
} from './Buttons'
import { EditOffer } from './EditOffer'
import { FillInformation } from './FillInformation'
import InfojobsLogo from './infojobs-logo.svg'
import { INITIAL_ID_VALUE, JobAttributes } from './utils'

interface Props {
  offer: InfojobsOfferValuesFragment | undefined
  installed: boolean
  jobAttributes: JobAttributes
}

export const Infojobs = ({ offer, installed, jobAttributes }: Props) => {
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showRepublishModal, setShowRepublishModal] = useState(false)
  const [showManageOfferModal, setShowManageOfferModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [createOffer, { loading: creatingOffer }] = useMutation(CreateOfferDocument, {
    extractorFn: (response) =>
      response.integrationsInfojobsMutations.createIntegrationsinfojobsOffer,
  })
  const { openDialog } = useConfirmationDialog()
  const { company } = useCurrentSession()

  const [unpublishOffer] = useMutation(UnpublishOfferDocument, {
    extractorFn: (response) =>
      response.integrationsInfojobsMutations.disableIntegrationsinfojobsOffer,
  })
  const [publishOffer] = useMutation(PublishOfferDocument, {
    extractorFn: (response) =>
      response.integrationsInfojobsMutations.enableIntegrationsinfojobsOffer,
  })

  const companyId = company.id
  const websiteUrl: string | undefined =
    offer?.externalId == null
      ? undefined
      : `https://www.infojobs.net/oferta.empleo?codigo=${encodeURIComponent(offer.externalId)}`

  const showSuccessMessage = () => {
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
    })
  }

  const handleOfferOperation = async (publish: boolean) => {
    if (offer === undefined) {
      return null
    }

    const variables = { companyId: companyId, offerId: offer.id }

    if (publish) {
      return handlePublishOffer(variables)
    }

    setShowManageOfferModal(false)
    return handleUnpublishOffer(variables)
  }

  const handleUnpublishOffer = async (variables: { companyId: string; offerId: string }) => {
    const response = await unpublishOffer({
      variables: variables,
      refetchQueries: [InfojobsOfferDocument],
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      showSuccessMessage()
    }
  }

  const handlePublishOffer = async (variables: { companyId: string; offerId: string }) => {
    const response = await publishOffer({
      variables: variables,
      refetchQueries: [InfojobsOfferDocument],
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      showSuccessMessage()
    }
  }

  const handleRepublishOffer = async (previousOffer: InfojobsOfferValuesFragment | undefined) => {
    if (previousOffer === undefined) {
      return null
    }

    const response = await createOffer({
      variables: {
        companyId: companyId,
        atsJobPostingId: jobAttributes.id,
        contractTypeId: previousOffer.contractTypeId,
        countryId: previousOffer.countryId,
        desiredSkills: previousOffer.desiredSkills,
        provinceId: previousOffer.provinceId,
        requiredSkills: previousOffer.requiredSkills,
        salaryFromId: previousOffer.salaryFromId,
        salaryPeriodId: previousOffer.salaryPerId,
        salaryToId: previousOffer.salaryToId,
        workingDayId: previousOffer.workingDayId,
        minimumExperienceId: previousOffer.minimumExperienceId,
        minimumStudiesId: previousOffer.minimumStudiesId,
        levelId: previousOffer.levelId ?? INITIAL_ID_VALUE,
        jobIndustryId: previousOffer.jobIndustryId,
        jobSubindustryId: previousOffer.jobSubindustryId,
        numberOfVacancies: 1,
        promotionCodes: previousOffer.promotionCodes,
      },
      refetchQueries: [InfojobsOfferDocument],
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      showSuccessMessage()
      setShowRepublishModal(false)
    }
  }

  const handleSuccessfulPublish = () => {
    setShowSuccessModal(true)
    setShowPublishModal(false)
  }

  const offerStatus = offer?.status

  const actionRequired = offerStatus == undefined
  const inProgress = offerStatus === Status.InProgress.toString()
  const offerPresent = offerStatus != undefined

  const offerNotPublished = offer?.externalId == null

  const offerStatusToCardStatus: Record<string, Status> = {
    active: Status.Published,
    in_progress: Status.InProgress,
    archived: Status.Offline,
    expired: Status.Expired,
    error: Status.Error,
  }

  const offerStatutsToAction: Record<string, JSX.Element> = {
    active: <ManageOfferButton onClick={() => setShowManageOfferModal(true)} />,
    archived: (
      <PublishButton
        onClick={() =>
          openDialog({
            title: i18n.t('ats.job_details.promotion.infojobs.job.open.title'),
            illustration: 'upload',
            content: i18n.t('ats.job_details.promotion.infojobs.job.open.description'),
            cancelLabel: i18n.t('common.cancel'),
            confirmLabel: i18n.t('common.accept'),
            onConfirm: () => handleOfferOperation(true),
          })
        }
      />
    ),
    error: (
      <RetryButton
        onClick={() => (offerNotPublished ? setShowRepublishModal(true) : setShowEditModal(true))}
      />
    ),
    expired: <OpenExpiredButton onClick={() => setShowRepublishModal(true)} />,
  }

  const handleEditRepublishOffer = () => {
    setShowRepublishModal(false)
    setShowPublishModal(true)
  }

  const handleEditOffer = () => {
    setShowManageOfferModal(false)
    setShowEditModal(true)
  }

  const status = installed
    ? actionRequired
      ? Status.ActionRequired
      : offerStatusToCardStatus[offerStatus]
    : undefined

  const action = inProgress ? undefined : installed ? (
    offerPresent ? (
      offerStatutsToAction[offerStatus]
    ) : (
      <FillMissingInformationButton onClick={() => onOpenFillInformation()} />
    )
  ) : (
    <IntegrationSetupButton integrationDatoId={integrationCodes.infojobs} />
  )

  const onCloseFillInformation = () => {
    track(trackingEvents.ats.infojobsPromotionCancelled, {
      company_id: companyId,
    })
    setShowPublishModal(false)
  }

  const onOpenFillInformation = () => {
    track(trackingEvents.ats.infojobsPromotionInitiated, {
      company_id: companyId,
    })
    setShowPublishModal(true)
  }

  const error = offer?.errorMessages ? offer?.errorMessages.join('. \n') : undefined

  return (
    <>
      <JobBoardCard
        title="Infojobs"
        description={i18n.t('ats.job_details.promotion.infojobs.description')}
        logo={InfojobsLogo}
        action={action}
        status={status}
        jobExternalUrl={websiteUrl}
        error={error}
      />

      {showPublishModal && (
        <FillInformation
          onClose={onCloseFillInformation}
          companyId={companyId}
          jobAttributes={jobAttributes}
          onSuccess={() => handleSuccessfulPublish()}
          offer={offer}
        />
      )}
      {showEditModal && offer && (
        <EditOffer
          companyId={companyId}
          jobAttributes={jobAttributes}
          offer={offer}
          onSuccess={() => setShowEditModal(false)}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showSuccessModal && (
        <Modal
          title={i18n.t('ats.job_details.promotion.infojobs.job.published.title')}
          description={i18n.t('ats.job_details.promotion.infojobs.job.published.description')}
          illustration={'upload'}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
      {showRepublishModal && (
        <Modal
          onClose={() => setShowRepublishModal(false)}
          title={i18n.t('ats.job_details.promotion.infojobs.job.republish.title')}
          description={i18n.t('ats.job_details.promotion.infojobs.job.republish.description')}
          illustration={'upload'}
          footer={
            <>
              <Button loading={creatingOffer} onClick={handleEditRepublishOffer} type="secondary">
                {i18n.t('ats.job_details.promotion.infojobs.job.republish.edit_information')}
              </Button>
              <Button onClick={() => handleRepublishOffer(offer)}>
                {i18n.t('common.confirm')}
              </Button>
            </>
          }
        />
      )}
      {showManageOfferModal && (
        <Modal
          onClose={() => setShowManageOfferModal(false)}
          title={i18n.t('ats.job_details.promotion.infojobs.job.manage.title')}
          description={i18n.t('ats.job_details.promotion.infojobs.job.manage.description')}
          illustration={'pencil'}
          footer={
            <>
              <Button onClick={() => handleOfferOperation(false)} type="secondary">
                {i18n.t('ats.job_details.promotion.job_boards.buttons.unpublish')}
              </Button>
              <Button loading={creatingOffer} onClick={handleEditOffer}>
                {i18n.t('ats.job_details.promotion.infojobs.job.republish.edit_information')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}
